import type { Point, SceneNode } from "../scene/model";
export interface PathAnchor extends Point {
  incoming?: Point;
  outgoing?: Point;
}
export interface VectorPath {
  anchors: PathAnchor[];
  closed: boolean;
  arrow: boolean;
  pen?: { amount: number; source: PathAnchor[]; width: number; height: number };
}
function validAnchors(anchors: PathAnchor[]) {
  return (
    Array.isArray(anchors) &&
    anchors.length >= 2 &&
    anchors.length <= 20000 &&
    anchors.every(
      (a) =>
        a &&
        [a, a.incoming, a.outgoing].every(
          (q) =>
            !q ||
            ([q.x, q.y].every(Number.isFinite) &&
              Math.abs(q.x) <= 1e6 &&
              Math.abs(q.y) <= 1e6),
        ),
    )
  );
}
export function vectorPath(node: SceneNode): VectorPath {
  return node.geometry.vector as VectorPath;
}
export function assertVector(node: SceneNode) {
  if (node.geometry.svgPath) {
    const s = node.geometry.svgPath as {
      d: string;
      x: number;
      y: number;
      width: number;
      height: number;
    };
    if (
      typeof s.d !== "string" ||
      s.d.length > 200000 ||
      !/^[\s\d.,+eEMmLlHhVvCcSsQqTtAaZz-]+$/.test(s.d) ||
      ![s.x, s.y, s.width, s.height].every(Number.isFinite) ||
      s.width <= 0 ||
      s.height <= 0
    )
      throw new Error("Повреждён векторный контур.");
    return;
  }
  const p = vectorPath(node);
  if (
    !p ||
    !validAnchors(p.anchors) ||
    typeof p.closed !== "boolean" ||
    typeof p.arrow !== "boolean" ||
    p.anchors.some((a) =>
      [a, a.incoming, a.outgoing].some(
        (q) =>
          q &&
          (![q.x, q.y].every(Number.isFinite) ||
            Math.abs(q.x) > 1e6 ||
            Math.abs(q.y) > 1e6),
      ),
    )
  )
    throw new Error("Повреждён векторный контур.");
  if (
    p.pen !== undefined &&
    (!p.pen ||
      p.arrow ||
      !Number.isInteger(p.pen.amount) ||
      p.pen.amount < 0 ||
      p.pen.amount > 100 ||
      !validAnchors(p.pen.source) ||
      (p.closed && p.pen.source.length < 3) ||
      ![p.pen.width, p.pen.height].every(
        (v) => Number.isFinite(v) && v >= 1 && v <= 1e6,
      ))
  )
    throw new Error("Повреждены параметры сглаживания ручки.");
}
/** Anchors are normalized to the node frame; resizing never converts paths to images. */
export function pathNode(
  id: string,
  anchors: PathAnchor[],
  closed = false,
  arrow = false,
): SceneNode {
  const points = pathExtrema(anchors, closed);
  const x = Math.min(...points.map((p) => p.x)),
    y = Math.min(...points.map((p) => p.y));
  const w = Math.max(1, Math.max(...points.map((p) => p.x)) - x),
    h = Math.max(1, Math.max(...points.map((p) => p.y)) - y);
  const norm = (p: Point) => ({
    x: (p.x - x) / w - 0.5,
    y: (p.y - y) / h - 0.5,
  });
  return {
    id,
    kind: "path",
    transform: {
      x: x + w / 2,
      y: y + h / 2,
      originX: "center",
      originY: "center",
    },
    geometry: {
      width: w,
      height: h,
      vector: {
        anchors: anchors.map((a) => ({
          ...norm(a),
          ...(a.incoming ? { incoming: norm(a.incoming) } : {}),
          ...(a.outgoing ? { outgoing: norm(a.outgoing) } : {}),
        })),
        closed,
        arrow,
      },
    },
    appearance: {
      fill: "#000000",
      fillEnabled: false,
      stroke: "#000000",
      strokeEnabled: true,
      strokeWidth: 3,
      opacity: 1,
      lineStyle: "solid",
    },
    text: {},
    interaction: { role: "decorShape" },
    effects: {},
    layer: { name: arrow ? "Стрелка" : "Линия" },
    media: {},
  };
}
export function pathPoints(node: SceneNode, steps = 24): Point[] {
  const p = vectorPath(node),
    a = p.anchors,
    out: Point[] = [];
  for (let i = 0; i < a.length - (p.closed ? 0 : 1); i++) {
    const start = a[i]!,
      end = a[(i + 1) % a.length]!,
      c1 = start.outgoing ?? start,
      c2 = end.incoming ?? end;
    const count = start.outgoing || end.incoming ? steps : 1;
    for (let j = 0; j < count; j++) {
      const t = j / count,
        u = 1 - t;
      out.push({
        x:
          u * u * u * start.x +
          3 * u * u * t * c1.x +
          3 * u * t * t * c2.x +
          t * t * t * end.x,
        y:
          u * u * u * start.y +
          3 * u * u * t * c1.y +
          3 * u * t * t * c2.y +
          t * t * t * end.y,
      });
    }
  }
  out.push(p.closed ? a[0]! : a[a.length - 1]!);
  return out;
}
/** Exact cubic extrema, including transformed controls supplied by the caller. */
export function pathExtrema(anchors: PathAnchor[], closed: boolean): Point[] {
  const points: Point[] = [...anchors];
  for (let i = 0; i < anchors.length - (closed ? 0 : 1); i++) {
    const a = anchors[i]!,
      b = anchors[(i + 1) % anchors.length]!,
      c = a.outgoing ?? a,
      d = b.incoming ?? b;
    for (const key of ["x", "y"] as const) {
      const A = -a[key] + 3 * c[key] - 3 * d[key] + b[key],
        B = 2 * (a[key] - 2 * c[key] + d[key]),
        C = c[key] - a[key];
      const det = B * B - 4 * A * C;
      const roots =
        Math.abs(A) < 1e-12
          ? Math.abs(B) < 1e-12
            ? []
            : [-C / B]
          : det < 0
            ? []
            : [
                (-B + Math.sqrt(det)) / (2 * A),
                (-B - Math.sqrt(det)) / (2 * A),
              ];
      for (const t of roots)
        if (t > 0 && t < 1) {
          const u = 1 - t;
          points.push({
            x:
              u * u * u * a.x +
              3 * u * u * t * c.x +
              3 * u * t * t * d.x +
              t * t * t * b.x,
            y:
              u * u * u * a.y +
              3 * u * u * t * c.y +
              3 * u * t * t * d.y +
              t * t * t * b.y,
          });
        }
    }
  }
  return points;
}
export function arrowPoints(node: SceneNode): Point[] {
  const p = vectorPath(node);
  if (!p.arrow) return [];
  const w = node.geometry.width!,
    h = node.geometry.height!,
    end = p.anchors.at(-1)!,
    prev = end.incoming ?? p.anchors.at(-2)!;
  const angle = Math.atan2((end.y - prev.y) * h, (end.x - prev.x) * w),
    size = Math.max(10, (node.appearance.strokeWidth ?? 3) * 4);
  return [
    end,
    ...[-0.45, 0.45].map((v) => ({
      x: end.x - (size * Math.cos(angle + v)) / w,
      y: end.y - (size * Math.sin(angle + v)) / h,
    })),
  ];
}

/** Fit the editable frame to the contour while retaining the object's affine basis. */
export function reframePath(node: SceneNode): SceneNode {
  const w = node.geometry.width!,
    h = node.geometry.height!,
    vector = vectorPath(node);
  const pixel = (p: Point) => ({ x: p.x * w, y: p.y * h });
  const frame = pathNode(
    node.id,
    vector.anchors.map((a) => ({
      ...pixel(a),
      ...(a.incoming ? { incoming: pixel(a.incoming) } : {}),
      ...(a.outgoing ? { outgoing: pixel(a.outgoing) } : {}),
    })),
    vector.closed,
    vector.arrow,
  );
  const angle = ((node.transform.rotation ?? 0) * Math.PI) / 180,
    c = Math.cos(angle),
    s = Math.sin(angle),
    k = Math.tan(((node.transform.skewX ?? 0) * Math.PI) / 180);
  const dx = frame.transform.x!,
    dy = frame.transform.y!;
  return {
    ...node,
    geometry: frame.geometry,
    transform: {
      ...node.transform,
      x: node.transform.x! + c * dx + (c * k - s) * dy,
      y: node.transform.y! + s * dx + (s * k + c) * dy,
    },
  };
}
